import { api } from '../lib/api';
import type {
  Job,
  JobFilters,
  JobSearchRequest,
  ApiResponse,
  PaginatedResponse,
  JobApplication,
  JobsAnalyticsResponse,
  SingleJobAnalyticsResponse,
} from '../types';

class JobService {
  // Normaliza resposta para formato PaginatedResponse (suporta ambos formatos do Laravel)
  private normalizePaginatedResponse(response: unknown): PaginatedResponse<Job> {
    const anyResp = response as Record<string, unknown>;

    // Formato Resource: { data: [], meta: { current_page, last_page, total, ... }, links: {} }
    if (anyResp.meta && typeof anyResp.meta === 'object') {
      const meta = anyResp.meta as Record<string, unknown>;
      return {
        data: (anyResp.data || []) as Job[],
        current_page: (meta.current_page as number) || 1,
        last_page: (meta.last_page as number) || 1,
        per_page: (meta.per_page as number) || 10,
        total: (meta.total as number) || 0,
        from: (meta.from as number) || 0,
        to: (meta.to as number) || 0,
      };
    }

    // Formato Paginate tradicional: { data: [], current_page, last_page, total, ... }
    return {
      data: (anyResp.data || []) as Job[],
      current_page: (anyResp.current_page as number) || 1,
      last_page: (anyResp.last_page as number) || 1,
      per_page: (anyResp.per_page as number) || 10,
      total: (anyResp.total as number) || 0,
      from: (anyResp.from as number) || 0,
      to: (anyResp.to as number) || 0,
    };
  }

  async getJobs(filters?: JobFilters): Promise<PaginatedResponse<Job>> {
    const response = await api.get<unknown>('/jobs', {
      params: filters as Record<string, string | number | boolean>,
    });
    return this.normalizePaginatedResponse(response);
  }

  async searchJobs(searchParams: JobSearchRequest): Promise<PaginatedResponse<Job>> {
    const response = await api.get<unknown>('/jobs/search', {
      params: searchParams as Record<string, string | number | boolean>,
    });
    return this.normalizePaginatedResponse(response);
  }

  async getFeaturedJobs(): Promise<Job[]> {
    const resp = await api.get<unknown>('/jobs/featured');
    // Aceita diferentes formatos de resposta do backend
    // - Array direta de jobs
    // - { data: Job[] }
    // - { data: { data: Job[] } } (paginada)
    // - { jobs: Job[] }
    if (Array.isArray(resp)) return resp as Job[];
    const anyResp = resp as Record<string, unknown>;
    if (Array.isArray(anyResp.data)) return anyResp.data as Job[];
    if (anyResp.data && Array.isArray((anyResp.data as Record<string, unknown>).data)) {
      return ((anyResp.data as Record<string, unknown>).data as Job[]);
    }
    if (Array.isArray(anyResp.jobs)) return anyResp.jobs as Job[];
    return [];
  }

  async getHomeJobs(): Promise<Job[]> {
    const resp = await api.get<unknown>('/jobs/home');
    if (Array.isArray(resp)) return resp as Job[];
    const anyResp = resp as Record<string, unknown>;
    if (Array.isArray(anyResp.data)) return anyResp.data as Job[];
    if (anyResp.data && Array.isArray((anyResp.data as Record<string, unknown>).data)) {
      return ((anyResp.data as Record<string, unknown>).data as Job[]);
    }
    if (Array.isArray(anyResp.jobs)) return anyResp.jobs as Job[];
    return [];
  }

  async getRecentJobs(limit = 10): Promise<Job[]> {
    const resp = await api.get<unknown>('/jobs/recent', { params: { limit } });
    if (Array.isArray(resp)) return resp as Job[];
    const anyResp = resp as Record<string, unknown>;
    if (Array.isArray(anyResp.data)) return anyResp.data as Job[];
    if (anyResp.data && Array.isArray((anyResp.data as Record<string, unknown>).data)) {
      return ((anyResp.data as Record<string, unknown>).data as Job[]);
    }
    if (Array.isArray(anyResp.jobs)) return anyResp.jobs as Job[];
    return [];
  }

  async getJobById(id: number): Promise<Job> {
    const response = await api.get<ApiResponse<Job> | Job>(`/jobs/${id}`);
    // Handle both wrapped { data: Job } and direct Job response
    if ('data' in response && response.data) {
      return response.data as Job;
    }
    return response as Job;
  }

  async getJobBySlug(slug: string): Promise<Job> {
    const response = await api.get<ApiResponse<Job>>(`/jobs/slug/${slug}`);
    return response.data;
  }

  async createJob(data: Partial<Job>): Promise<Job> {
    const response = await api.post<ApiResponse<Job>>('/jobs', data);
    return response.data;
  }

  async updateJob(id: number, data: Partial<Job>): Promise<Job> {
    const response = await api.put<ApiResponse<Job>>(`/jobs/${id}`, data);
    return response.data;
  }

  async deleteJob(id: number): Promise<void> {
    await api.delete(`/jobs/${id}`);
  }

  async pauseJob(id: number): Promise<Job> {
    const response = await api.patch<ApiResponse<Job>>(`/jobs/${id}/pause`);
    return response.data;
  }

  async activateJob(id: number): Promise<Job> {
    const response = await api.patch<ApiResponse<Job>>(`/jobs/${id}/activate`);
    return response.data;
  }

  async closeJob(id: number): Promise<Job> {
    const response = await api.patch<ApiResponse<Job>>(`/jobs/${id}/close`);
    return response.data;
  }

  async fillJob(id: number): Promise<Job> {
    const response = await api.patch<ApiResponse<Job>>(`/jobs/${id}/fill`);
    return response.data;
  }

  async repostJob(id: number, days?: number): Promise<Job> {
    const response = await api.patch<ApiResponse<Job>>(`/jobs/${id}/repost`, days ? { days } : {});
    return response.data;
  }

  async incrementView(id: number): Promise<void> {
    await api.post(`/jobs/${id}/view`);
  }

  async applyToJob(jobId: number, data: { cover_letter?: string; resume_file?: File }): Promise<JobApplication> {
    if (data.resume_file) {
      const response = await api.uploadFile<ApiResponse<JobApplication>>(
        `/jobs/${jobId}/apply`,
        data.resume_file,
        { cover_letter: data.cover_letter || '' }
      );
      return response.data;
    }

    const response = await api.post<ApiResponse<JobApplication>>(`/jobs/${jobId}/apply`, {
      cover_letter: data.cover_letter,
    });
    return response.data;
  }

  // Candidatura simplificada - envia apenas nome e email sem validar perfil completo
  async applyToJobSimple(
    jobId: number,
    applicantName: string,
    applicantEmail: string
  ): Promise<JobApplication> {
    const response = await api.post<ApiResponse<JobApplication>>(`/jobs/${jobId}/apply`, {
      skip_profile_validation: true,
      applicant_name: applicantName,
      applicant_email: applicantEmail,
    });
    return response.data;
  }

  async getJobApplications(jobId: number): Promise<JobApplication[]> {
    const response = await api.get<ApiResponse<JobApplication[]>>(`/jobs/${jobId}/applications`);
    return response.data;
  }

  async updateApplicationStatus(
    jobId: number,
    applicationId: number,
    status: 'pending' | 'reviewed' | 'approved' | 'rejected'
  ): Promise<JobApplication> {
    const response = await api.patch<ApiResponse<JobApplication>>(
      `/jobs/${jobId}/applications/${applicationId}`,
      { status }
    );
    return response.data;
  }

  async getAreas(): Promise<string[]> {
    const response = await api.get<ApiResponse<string[]>>('/jobs/areas');
    return response.data;
  }

  async getCities(): Promise<string[]> {
    const response = await api.get<ApiResponse<string[]>>('/jobs/cities');
    return response.data;
  }

  async getAnalytics(startDate?: string, endDate?: string): Promise<JobsAnalyticsResponse> {
    const params: Record<string, string> = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    const response = await api.get<ApiResponse<JobsAnalyticsResponse>>('/jobs/analytics', { params });
    return response.data;
  }

  async getJobAnalytics(jobId: number, startDate?: string, endDate?: string): Promise<SingleJobAnalyticsResponse> {
    const params: Record<string, string> = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    const response = await api.get<ApiResponse<SingleJobAnalyticsResponse>>(`/jobs/${jobId}/analytics`, { params });
    return response.data;
  }
}

export const jobService = new JobService();
